import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import Map, { Layer, MapRef, Source } from 'react-map-gl';
import type { LayerProps } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { MAPBOX_ACCESS_TOKEN } from '../../consts';

const mapStyles = { width: '100%', height: '100%' };
const createMarkerLayer = (markerColor: string): LayerProps => ({
  id: 'preview-find-position',
  type: 'circle',
  paint: {
    'circle-radius': 6,
    'circle-color': markerColor,
    'circle-stroke-color': '#fff',
    'circle-stroke-width': 2,
  },
});

const PreviewMap = ({
  longitude,
  latitude,
  markerColor,
  interactive = true,
}: {
  longitude: number;
  latitude: number;
  markerColor: string;
  /** When false, the map ignores dragging, zooming and other gestures. */
  interactive?: boolean;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapRef>(null);
  const [viewState, setViewState] = React.useState({
    longitude,
    latitude,
    zoom: 15,
  });
  const markerData = useMemo(
    () => ({
      type: 'Feature' as const,
      properties: {},
      geometry: {
        type: 'Point' as const,
        coordinates: [longitude, latitude],
      },
    }),
    [longitude, latitude]
  );
  const markerLayer = useMemo(() => createMarkerLayer(markerColor), [
    markerColor,
  ]);

  useEffect(() => {
    setViewState((prev) => ({ longitude, latitude, zoom: prev.zoom }));
  }, [longitude, latitude]);

  const resizeMap = useCallback(() => {
    mapRef.current?.resize();
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    let secondFrame: number | null = null;
    const firstFrame = window.requestAnimationFrame(() => {
      resizeMap();
      secondFrame = window.requestAnimationFrame(resizeMap);
    });
    const observer = new ResizeObserver(resizeMap);
    observer.observe(container);

    return () => {
      window.cancelAnimationFrame(firstFrame);
      if (secondFrame != null) {
        window.cancelAnimationFrame(secondFrame);
      }
      observer.disconnect();
    };
  }, [resizeMap]);

  return (
    <div ref={containerRef} style={mapStyles}>
      <Map
        {...viewState}
        ref={mapRef}
        interactive={interactive}
        onMove={(evt) => setViewState(evt.viewState)}
        mapStyle="mapbox://styles/mapbox/satellite-v9"
        mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
        style={mapStyles}
      >
        <Source type="geojson" data={markerData}>
          <Layer {...markerLayer} />
        </Source>
      </Map>
    </div>
  );
};

export { PreviewMap };
